import { isInIframe } from '@/hooks';
import { vSensors } from '@/utils/sevsors';
import { FlowSdyy } from '@clouddesign/sdyy-condition';
import { useSyncExternalStore } from 'react';

export const FINISHED_MATERIAL_BADCASE_EVENT = 'finished_material_badcase';
const isSearchBadcaseDebug = () =>
  new URLSearchParams(window.location.search).get('debugSearchBadcase') === '1';
const canUseSearchBadcase = () => isInIframe || isSearchBadcaseDebug();

export const SEARCH_BADCASE_REASONS = [
  '结果不符合预期',
  '不知道搜什么词',
  '搜图结果不相似',
  '其他',
] as const;

export type SearchBadcaseReason = (typeof SEARCH_BADCASE_REASONS)[number];

const IMAGE_SEARCH_METHODS = ['以图搜图', '图片+文字'];

export interface FinishedMaterialSearchContext {
  search_method: string;
  key_word: string;
  moudule_type: string;
  page_title: string;
  filter_material_type: string;
  filter_material_type_id: string;
  material_type: string;
  filter_cate: string;
  label_type: string;
  label_id: string;
  label_name: string;
  result_count: string;
  current_page: string;
  material_id_list: string;
  click_material_id_list: string;
  image_id: string;
  image_search_main_category: string;
  image_search_target_object: string;
  image_search_structure_tag: string;
  image_search_material_tag: string;
  image_search_style_tag: string;
  image_search_other_tag: string;
}

interface SearchBadcaseState {
  enabled: boolean;
  visible: boolean;
  expanded: boolean;
}

interface SearchResultInput {
  context: FinishedMaterialSearchContextInput;
  resultCount: number | null; // null 表示总数未知，不满足空结果/末页条件。
  currentPage: number;
  pageSize: number;
  materialIds?: unknown[];
  isPagination?: boolean;
}

type FinishedMaterialSearchContextInput = {
  [Key in keyof Omit<
    FinishedMaterialSearchContext,
    | 'result_count'
    | 'current_page'
    | 'material_id_list'
    | 'click_material_id_list'
  >]: unknown;
};

const EMPTY_STATE: SearchBadcaseState = {
  enabled: false,
  visible: false,
  expanded: false,
};

let state = EMPTY_STATE;
let currentContext: FinishedMaterialSearchContext | undefined;
let previousKeyword = '';
let previousImageId = '';
let replacementCount = 0;
let hasUsedSinceReplacement = false;
let hasReportedInCurrentSearch = false;
let currentPageSize = 1;
let suppressNextSearchBadcase = false;
/** 同一原图下切换识别缩略图或分类标签时冻结受挫入口，连空结果也不更新。 */
let preserveSearchBadcaseOnOriginRefinement = false;
let switchPromise: Promise<boolean> | undefined;
let lastDebugSignature = '';
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

const setState = (next: Partial<SearchBadcaseState>) => {
  state = { ...state, ...next };
  const debugInfo = {
    换词次数: replacementCount,
    是否使用过素材: hasUsedSinceReplacement ? '是' : '否',
    搜索方式: currentContext?.search_method || '无',
    当前页: currentContext?.current_page || '无',
  };
  const debugSignature = JSON.stringify(debugInfo);
  if (debugSignature !== lastDebugSignature) {
    lastDebugSignature = debugSignature;
    console.log(
      `[搜索受挫][成品素材广场] 换词次数: ${debugInfo.换词次数}, 是否使用过素材: ${debugInfo.是否使用过素材}, 搜索方式: ${debugInfo.搜索方式}, 当前页: ${debugInfo.当前页}`,
    );
  }
  emit();
};

const toStringValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined && item !== null)
      .map((item) => String(item))
      .join(',');
  }
  return value === undefined || value === null ? '' : String(value);
};

const toMaterialIdList = (materialIds?: unknown[]) =>
  (materialIds || [])
    .map(
      (item: any) =>
        item?.id ?? item?.materialLibraryId ?? item?.MaterialId ?? item,
    )
    .filter((item) => item !== undefined && item !== null && item !== '')
    .map((item) => String(item))
    .join(',');

const normalizeContext = (context: FinishedMaterialSearchContextInput) => {
  const result = {} as Record<string, string>;
  Object.entries(context).forEach(([key, value]) => {
    result[key] = toStringValue(value);
  });
  return result as Omit<
    FinishedMaterialSearchContext,
    | 'result_count'
    | 'current_page'
    | 'material_id_list'
    | 'click_material_id_list'
  >;
};

const shouldShow = () => {
  const isImageSearchMethod = IMAGE_SEARCH_METHODS.includes(
    currentContext?.search_method || '',
  );
  if (
    (!state.enabled && !isSearchBadcaseDebug()) ||
    !canUseSearchBadcase() ||
    hasReportedInCurrentSearch ||
    !currentContext ||
    (!currentContext.key_word.trim() && !isImageSearchMethod) ||
    !['关键词搜索', ...IMAGE_SEARCH_METHODS].includes(
      currentContext.search_method,
    )
  ) {
    return false;
  }
  const currentPage = Number(currentContext.current_page);
  const resultCount =
    currentContext.result_count === ''
      ? null
      : Number(currentContext.result_count);
  // 总数仅一条视为精准命中，优先于末页和换词触发，不重置搜索计数。
  if (resultCount === 1) return false;
  const pageSize = Math.max(1, currentPageSize);
  const totalPages =
    resultCount !== null && resultCount > 0
      ? Math.ceil(resultCount / pageSize)
      : 0;
  return (
    currentPage >= 4 ||
    (resultCount === 0 && currentPage === 1) ||
    (totalPages > 0 && totalPages <= 3 && currentPage === totalPages) ||
    (replacementCount >= 3 && !hasUsedSinceReplacement)
  );
};

export const initSearchBadcaseSwitch = async () => {
  if (!canUseSearchBadcase()) {
    setState({ enabled: false, visible: false });
    return false;
  }
  if (switchPromise) return switchPromise;
  switchPromise = (async () => {
    try {
      await FlowSdyy.initData();
      const enabled =
        isSearchBadcaseDebug() ||
        Boolean(await FlowSdyy.checkFlowdiagramCondition('seo_badcase'));
      setState({ enabled, visible: enabled && shouldShow() });
      return enabled;
    } catch (error) {
      console.error('Failed to initialize search badcase switch:', error);
      setState({ enabled: false, visible: false });
      return false;
    }
  })();
  return switchPromise;
};

export const subscribeSearchBadcase = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getSearchBadcaseState = () => state;

export const useSearchBadcaseState = () =>
  useSyncExternalStore(
    subscribeSearchBadcase,
    getSearchBadcaseState,
    getSearchBadcaseState,
  );

export const hideSearchBadcase = () =>
  setState({ visible: false, expanded: false });

export const resetSearchBadcaseSession = () => {
  currentContext = undefined;
  previousKeyword = '';
  previousImageId = '';
  replacementCount = 0;
  hasUsedSinceReplacement = false;
  hasReportedInCurrentSearch = false;
  currentPageSize = 1;
  suppressNextSearchBadcase = false;
  preserveSearchBadcaseOnOriginRefinement = false;
  setState({ visible: false, expanded: false });
};

export const recordFinishedMaterialSearch = (input: SearchResultInput) => {
  const normalizedContext = normalizeContext(input.context);
  const keyword = normalizedContext.key_word.trim();
  const previousSearchMethod = currentContext?.search_method || '';
  const currentSearchMethod = normalizedContext.search_method;
  const isImageSearch = IMAGE_SEARCH_METHODS.includes(currentSearchMethod);
  const isPreviousImageSearch =
    IMAGE_SEARCH_METHODS.includes(previousSearchMethod);
  const imageId = normalizedContext.image_id.trim();
  const isLeavingImageSearchToEmpty =
    isPreviousImageSearch && !currentSearchMethod && !keyword;
  const hasPreviousSearch = Boolean(
    currentContext &&
      (previousSearchMethod || previousKeyword || previousImageId),
  );
  const isSearchChange =
    hasPreviousSearch &&
    !isLeavingImageSearchToEmpty &&
    // 连续纯以图搜图仅在原图发生变化时累计，同一张原图重复搜索不累计。
    ((Boolean(keyword) && previousKeyword !== keyword) ||
      (Boolean(currentSearchMethod) &&
        currentSearchMethod !== previousSearchMethod) ||
      (isImageSearch &&
        isPreviousImageSearch &&
        Boolean(imageId || previousImageId) &&
        imageId !== previousImageId));
  const isSameOriginImageRefinement =
    Boolean(currentContext) &&
    isImageSearch &&
    IMAGE_SEARCH_METHODS.includes(currentContext.search_method) &&
    Boolean(imageId) &&
    imageId === currentContext.image_id.trim() &&
    keyword === currentContext.key_word.trim() &&
    currentSearchMethod === currentContext.search_method;
  // 原图、搜索方式和关键词都未变：切缩略图/标签只换结果，受挫入口保持原状。
  if (isSameOriginImageRefinement) {
    if (!input.isPagination && input.currentPage === 1) {
      preserveSearchBadcaseOnOriginRefinement = true;
      return;
    }
    if (preserveSearchBadcaseOnOriginRefinement) {
      return;
    }
  } else {
    preserveSearchBadcaseOnOriginRefinement = false;
  }
  let suppressCurrentSearch = false;
  if (input.currentPage === 1) {
    if (!input.isPagination) {
      suppressCurrentSearch = suppressNextSearchBadcase;
      suppressNextSearchBadcase = false;
      // 从以图搜图点击返回或清空图片，产生一次空搜索，但不打断连续换词统计。
      if (!keyword && !isLeavingImageSearchToEmpty && !isImageSearch) {
        replacementCount = 0;
      }
      if (isSearchChange) {
        replacementCount += 1;
      }
      if (!isLeavingImageSearchToEmpty) {
        if (!isImageSearch) {
          previousKeyword = keyword;
          previousImageId = '';
        } else {
          // 图文搜索也记录最新有效词；纯图搜索保留此前有效词供退出后延续。
          if (keyword) previousKeyword = keyword;
          previousImageId = imageId;
        }
        hasUsedSinceReplacement = false;
      }
      hasReportedInCurrentSearch = false;
    }
    currentPageSize = Math.max(1, Number(input.pageSize) || 1);
    currentContext = {
      ...(input.isPagination && currentContext
        ? currentContext
        : normalizedContext),
      result_count: toStringValue(input.resultCount),
      current_page: '1',
      material_id_list: toMaterialIdList(input.materialIds),
      click_material_id_list: '',
    };
    setState({ visible: false, expanded: false });
  } else if (currentContext) {
    currentContext = {
      ...currentContext,
      result_count: toStringValue(input.resultCount),
      current_page: toStringValue(input.currentPage),
      material_id_list: toMaterialIdList(input.materialIds),
    };
  }
  setState({ visible: suppressCurrentSearch ? false : shouldShow() });
};

export const updateFinishedMaterialPage = (
  currentPage: number,
  materialIds?: unknown[],
) => {
  if (!currentContext || preserveSearchBadcaseOnOriginRefinement) return;
  currentContext = {
    ...currentContext,
    current_page: toStringValue(currentPage),
    material_id_list: toMaterialIdList(materialIds),
  };
  setState({ visible: shouldShow() });
};

export const recordFinishedMaterialClick = (materialId: unknown) => {
  if (
    !currentContext ||
    materialId === undefined ||
    materialId === null ||
    materialId === ''
  ) {
    return;
  }
  const ids = currentContext.click_material_id_list
    ? currentContext.click_material_id_list.split(',')
    : [];
  const id = String(materialId);
  if (!ids.includes(id)) {
    ids.push(id);
    currentContext.click_material_id_list = ids.join(',');
  }
};

export const markFinishedMaterialUsed = () => {
  if (!currentContext) return;
  hasUsedSinceReplacement = true;
  replacementCount = 0;
  setState({ visible: false, expanded: false });
};

export const toggleSearchBadcaseMenu = () => {
  if (state.visible) setState({ expanded: !state.expanded });
};

const openOnlineCustomerService = () => {
  try {
    const targetWindow = window.parent || window;
    targetWindow.postMessage(
      {
        origin: 'uiapi.materialUiplugin',
        data: {
          name: 'topMenuBar',
          action: 'onClickHandler',
          type: 'home',
          params: { id: 'onlineCustomerService' },
        },
      },
      '*',
    );
    return true;
  } catch (error) {
    console.error(
      'Failed to notify materialUi to open online customer service:',
      error,
    );
    return false;
  }
};

export const reportFinishedMaterialBadcase = (reason: SearchBadcaseReason) => {
  if ((!state.enabled && !isSearchBadcaseDebug()) || !currentContext)
    return false;
  // 反馈提交后立即收起入口，不等待埋点或客服弹窗调用完成。
  hasReportedInCurrentSearch = true;
  replacementCount = 0;
  suppressNextSearchBadcase = true;
  setState({ visible: false, expanded: false });
  try {
    vSensors?.track(FINISHED_MATERIAL_BADCASE_EVENT, {
      reason: toStringValue(reason),
      ...currentContext,
    });
  } catch (error) {
    console.error('Failed to track finished material badcase:', error);
  }
  openOnlineCustomerService();
  return true;
};
